import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { isSuperAdminOrOwner } from '@jira-clone/shared-types'
import type { PermissionEntry } from '../components/PermissionToggleGrid'

export interface PermissionTemplate {
  id: string
  departmentId: string
  departmentName: string
  role: string
  permissions: PermissionEntry[]
}

export function usePermissionTemplates() {
  const { organization, user } = useAuth()
  return useQuery({
    queryKey: ['permission-templates', organization?.id],
    queryFn: async () => {
      const res = await api.get<any>('/permission-templates', { organizationId: organization?.id })
      return (res?.data || []) as PermissionTemplate[]
    },
    enabled: !!organization?.id && isSuperAdminOrOwner(user?.role),
    staleTime: 60 * 1000,
  })
}

export function useUpdatePermissionTemplate() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: PermissionEntry[] }) =>
      api.patch(`/permission-templates/${id}`, { permissions }, { skipErrorToast: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permission-templates', organization?.id] })
    },
  })
}

// The only 4 role tiers ever actually stored (see the backend's identical
// STORABLE_ROLES comment) — "Superadmin" is a display label for OWNER, not
// a separate storable value.
export const STORABLE_ROLES = ['OWNER', 'ADMIN', 'MEMBER', 'VIEWER'] as const

export function useCreatePermissionTemplate() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { departmentId: string; role: (typeof STORABLE_ROLES)[number]; permissions?: PermissionEntry[] }) =>
      api.post<{ id: string; departmentName: string; role: string; appliedToExistingUsers: number; skippedCustomizedUsers: number }>(
        '/permission-templates',
        { ...payload, organizationId: organization?.id },
        { skipErrorToast: true }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permission-templates', organization?.id] })
    },
  })
}

// Pushes a template's permissions out to current employees already in that
// department + role combo — anyone whose access was manually customized via
// Manage Access since is skipped, never overwritten. A separate, explicit
// action from saving the template itself.
export function useApplyPermissionTemplate() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ message: string; applied: number; skipped: number }>(
        `/permission-templates/${id}/apply`,
        {},
        { skipErrorToast: true }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permission-templates', organization?.id] })
    },
  })
}

export function useDeletePermissionTemplate() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<any>(`/permission-templates/${id}`, { skipErrorToast: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permission-templates', organization?.id] })
    },
  })
}
